import { toDateInput } from '../utils/status-flow-dates';

export type TokenAudience = 'ROLE' | 'PARTY' | null;

/** Referencias livianas usadas en el editor. */
export interface FlowStepStatusRef {
  uuid?: string;
  slug?: string;
  id?: number;
  name?: string;
  code?: number;
}

export interface FlowStepDocumentRef {
  slug?: string;
  name?: string;
}

/** Contrato enviado al API al guardar el flujo. */
export interface FlowStepPayload {
  statusUuid: string | null;
  statusSlug: string | null;
  statusId: number | null;
  stepSequence: number;
  isActive?: boolean | null;
  requiredRole: string | null;
  requiredRoles: string[] | null;
  alternateRole: string | null;
  alternateRoleUntil: string | null;
  contentReviewRole: string | null;
  allowsToken: boolean;
  tokenAudience: TokenAudience;
  tokenConfirmStatusSlug: string | null;
  tokenRejectStatusSlug: string;
  notifyRole: boolean;
  requiresDocument: boolean;
  documentTypeSlug: string | null;
  allowsObserve: boolean;
  observeToStatusSlug: string | null;
  resubmitToStatusSlug: string | null;
  requiresSession?: boolean | null;
}

/**
 * Modelo editable de un paso BPM en el mantenedor.
 * Encapsula reglas de rol, token y serialización a payload.
 */
export class FlowStepEditorModel {
  statusUuid: string | null = null;
  statusSlug: string | null = null;
  statusId: number | null = null;
  status: FlowStepStatusRef | null = null;
  stepSequence = 1;
  isActive = true;
  requiredRole: string | null = null;
  requiredRoles: string[] = [];
  alternateRole: string | null = null;
  alternateRoleUntil: string | null = null;
  contentReviewRole: string | null = null;
  allowsToken = false;
  tokenAudience: TokenAudience = null;
  tokenConfirmStatusSlug: string | null = null;
  tokenRejectStatusSlug = 'REJECTED';
  notifyRole = false;
  requiresDocument = false;
  documentTypeSlug: string | null = null;
  documentType: FlowStepDocumentRef | null = null;
  allowsObserve = false;
  observeToStatusSlug: string | null = 'OBSERVED';
  resubmitToStatusSlug: string | null = null;
  requiresSession = false;

  /** Crea un paso vacío para agregar al flujo. */
  static empty(stepSequence: number): FlowStepEditorModel {
    const step = new FlowStepEditorModel();
    step.stepSequence = stepSequence;
    return step;
  }

  /** Hidrata desde un paso del API / catálogo. */
  static fromApi(raw: any): FlowStepEditorModel {
    const step = new FlowStepEditorModel();
    step.statusUuid = raw.status?.uuid ?? raw.statusUuid ?? null;
    step.statusSlug = raw.status?.slug ?? raw.statusSlug ?? null;
    step.statusId = raw.statusId ?? raw.status?.id ?? null;
    step.status = raw.status ?? null;
    step.stepSequence = raw.stepSequence ?? 1;
    step.isActive = raw.isActive !== false;
    step.requiredRoles = Array.isArray(raw.requiredRoles) && raw.requiredRoles.length
      ? raw.requiredRoles.map(String)
      : (raw.requiredRole ? [String(raw.requiredRole)] : []);
    step.requiredRole = step.requiredRoles[0] ?? raw.requiredRole ?? null;
    step.alternateRole = raw.alternateRole ?? null;
    step.alternateRoleUntil = toDateInput(raw.alternateRoleUntil);
    step.contentReviewRole = raw.contentReviewRole ?? null;
    step.allowsToken = !!raw.allowsToken;
    step.tokenAudience = raw.tokenAudience ?? null;
    step.tokenConfirmStatusSlug = raw.tokenConfirmStatusSlug ?? null;
    step.tokenRejectStatusSlug = raw.tokenRejectStatusSlug ?? 'REJECTED';
    step.notifyRole = raw.requiredRole ? (raw.notifyRole ?? true) : false;
    step.requiresDocument = !!raw.requiresDocument;
    step.documentTypeSlug = raw.documentTypeSlug ?? raw.documentType?.slug ?? null;
    step.documentType = raw.documentType ?? null;
    step.allowsObserve = !!(raw.contentReviewRole || raw.allowsObserve);
    step.observeToStatusSlug = raw.observeToStatusSlug ?? 'OBSERVED';
    step.resubmitToStatusSlug = raw.resubmitToStatusSlug ?? null;
    step.requiresSession = raw.requiresSession === true;
    return step;
  }

  /** Limpia campos de token al desactivar enlace público. */
  clearTokenFields(): void {
    this.tokenAudience = null;
    this.tokenConfirmStatusSlug = null;
    this.tokenRejectStatusSlug = 'REJECTED';
  }

  /** Defaults al activar enlace público. */
  applyTokenDefaults(nextConfirmSlug: string | null): void {
    this.tokenAudience = this.requiredRole ? 'ROLE' : 'PARTY';
    this.tokenRejectStatusSlug = this.tokenRejectStatusSlug || 'REJECTED';
    if (!this.tokenConfirmStatusSlug) this.tokenConfirmStatusSlug = nextConfirmSlug;
  }

  /** Ajusta notify / extraordinario / token según el rol requerido. */
  syncRequiredRoleSideEffects(): void {
    this.clearRoleWhenEmpty();
    this.clearAlternateIfSameAsRequired();
    this.downgradeTokenAudienceWithoutRole();
  }

  private clearRoleWhenEmpty(): void {
    if (this.requiredRoles.length || this.requiredRole) return;
    this.notifyRole = false;
    this.alternateRole = null;
    this.alternateRoleUntil = null;
  }

  private clearAlternateIfSameAsRequired(): void {
    if (!this.alternateRole) return;
    if (!this.requiredRoles.includes(this.alternateRole) && this.alternateRole !== this.requiredRole) return;
    this.alternateRole = null;
    this.alternateRoleUntil = null;
  }

  /** Sincroniza requiredRole legacy con el array. */
  syncRequiredRolesFromArray(): void {
    this.requiredRole = this.requiredRoles[0] ?? null;
    this.syncRequiredRoleSideEffects();
  }

  private downgradeTokenAudienceWithoutRole(): void {
    if (!this.allowsToken || this.tokenAudience !== 'ROLE' || this.requiredRole) return;
    this.tokenAudience = 'PARTY';
  }

  /** ¿Habilitación extraordinaria vigente? */
  isExtraordinaryActive(): boolean {
    const role = String(this.alternateRole || '').trim();
    const until = toDateInput(this.alternateRoleUntil);
    if (!role || !until) return false;
    const end = new Date(`${until}T23:59:59.999`);
    return Number.isFinite(end.getTime()) && Date.now() <= end.getTime();
  }

  /** Serializa a payload de API (paso terminal limpia controles). */
  toPayload(terminal: boolean, defaultUntil: string): FlowStepPayload {
    if (terminal) return this.toTerminalPayload();
    return this.toActivePayload(defaultUntil);
  }

  private toTerminalPayload(): FlowStepPayload {
    return {
      statusUuid: this.statusUuid || this.status?.uuid || null,
      statusSlug: this.statusSlug || this.status?.slug || null,
      statusId: this.statusId ?? this.status?.id ?? null,
      stepSequence: this.stepSequence,
      isActive: this.isActive !== false,
      requiresSession: this.requiresSession === true,
      requiredRole: this.notifyRole ? this.requiredRole : null,
      requiredRoles: null,
      alternateRole: null,
      alternateRoleUntil: null,
      contentReviewRole: null,
      allowsToken: false,
      tokenAudience: null,
      tokenConfirmStatusSlug: null,
      tokenRejectStatusSlug: 'REJECTED',
      notifyRole: !!this.requiredRole && !!this.notifyRole,
      requiresDocument: false,
      documentTypeSlug: null,
      allowsObserve: false,
      observeToStatusSlug: null,
      resubmitToStatusSlug: null
    };
  }

  private toActivePayload(defaultUntil: string): FlowStepPayload {
    const hasReviewer = !!this.contentReviewRole;
    return {
      statusUuid: this.statusUuid || this.status?.uuid || null,
      statusSlug: this.statusSlug || this.status?.slug || null,
      statusId: this.statusId ?? this.status?.id ?? null,
      stepSequence: this.stepSequence,
      isActive: this.isActive !== false,
      requiresSession: this.requiresSession === true,
      requiredRole: this.requiredRoles[0] ?? this.requiredRole,
      requiredRoles: this.requiredRoles.length ? [...this.requiredRoles] : null,
      alternateRole: this.requiredRoles.length ? (this.alternateRole || null) : null,
      alternateRoleUntil: this.resolveAlternateUntil(defaultUntil),
      contentReviewRole: this.contentReviewRole || null,
      allowsToken: !!this.allowsToken,
      tokenAudience: this.allowsToken ? this.tokenAudience : null,
      tokenConfirmStatusSlug: this.allowsToken ? this.tokenConfirmStatusSlug : null,
      tokenRejectStatusSlug: this.allowsToken ? (this.tokenRejectStatusSlug || 'REJECTED') : 'REJECTED',
      notifyRole: !!this.requiredRole && !!this.notifyRole,
      requiresDocument: !!this.requiresDocument,
      documentTypeSlug: this.requiresDocument ? (this.documentTypeSlug ?? null) : null,
      // Observar = tener rol revisor (sin switch aparte)
      allowsObserve: hasReviewer,
      observeToStatusSlug: hasReviewer ? (this.observeToStatusSlug || 'OBSERVED') : null,
      resubmitToStatusSlug: hasReviewer ? (this.resubmitToStatusSlug || null) : null
    };
  }

  private resolveAlternateUntil(defaultUntil: string): string | null {
    if (!this.requiredRole || !this.alternateRole) return null;
    return this.alternateRoleUntil || defaultUntil;
  }
}

/** Siguiente número de secuencia. */
export function nextStepSequence(flows: FlowStepEditorModel[]): number {
  if (!flows.length) return 1;
  return Math.max(...flows.map((f) => f.stepSequence)) + 1;
}

export function isTerminalStepIndex(flowsLength: number, index: number): boolean {
  return flowsLength > 1 && index === flowsLength - 1;
}

/** Último paso exitoso del BPM: siempre APPROVED (consultas / reportes). */
export const FIXED_FINAL_STATUS_SLUG = 'APPROVED';

export function statusSlugOf(flow: FlowStepEditorModel | null | undefined): string {
  return String(flow?.statusSlug || flow?.status?.slug || '').toUpperCase();
}

export function isFixedFinalStep(flow: FlowStepEditorModel | null | undefined): boolean {
  return statusSlugOf(flow) === FIXED_FINAL_STATUS_SLUG;
}

/** ¿El índice es el cierre fijo APPROVED? */
export function isFixedFinalIndex(flows: FlowStepEditorModel[], index: number): boolean {
  return flows.length > 0 && index === flows.length - 1 && isFixedFinalStep(flows[index]);
}

/** Aplica el estado APPROVED del catálogo a un paso. */
export function bindApprovedStatus(step: FlowStepEditorModel, approved: any): void {
  if (!approved) return;
  step.statusUuid = approved.uuid ?? null;
  step.statusId = approved.id ?? null;
  step.statusSlug = FIXED_FINAL_STATUS_SLUG;
  step.status = approved;
  step.requiresDocument = false;
  step.documentTypeSlug = null;
  step.documentType = null;
  step.allowsToken = false;
  step.clearTokenFields();
}

/**
 * Garantiza que el flujo termine en APPROVED (fijo, no movible).
 * - Si ya hay APPROVED al final, lo deja y limpia controles de cierre.
 * - Si APPROVED está en medio, lo mueve al final.
 * - Si no hay APPROVED, lo agrega al final (requiere ref de catálogo).
 */
export function ensureApprovedFinalStep(
  flows: FlowStepEditorModel[],
  approvedStatus: any | null | undefined
): FlowStepEditorModel[] {
  if (!flows?.length) {
    if (!approvedStatus) return flows ?? [];
    const final = FlowStepEditorModel.empty(1);
    bindApprovedStatus(final, approvedStatus);
    return [final];
  }

  const list = [...flows];
  const approvedIdx = list.findIndex((f) => isFixedFinalStep(f));

  if (approvedIdx >= 0) {
    const [approvedStep] = list.splice(approvedIdx, 1);
    if (approvedStatus) bindApprovedStatus(approvedStep, approvedStatus);
    else {
      approvedStep.statusSlug = FIXED_FINAL_STATUS_SLUG;
      approvedStep.requiresDocument = false;
      approvedStep.documentTypeSlug = null;
      approvedStep.allowsToken = false;
      approvedStep.clearTokenFields();
    }
    list.push(approvedStep);
  } else if (approvedStatus) {
    const final = FlowStepEditorModel.empty(list.length + 1);
    bindApprovedStatus(final, approvedStatus);
    list.push(final);
  }

  list.forEach((f, i) => { f.stepSequence = i + 1; });
  return list;
}
